import React from 'react';
import Colors from '../../../constants/Colors';
import Point from './Point';
import {
  alternateComparedLineColor, alternateLineColor, selectedLineColor,
} from '../../AirQualityGraph';
import { comparisonLineColor } from '../../../components/Home/HomeGraphLineSelector';
import { ChartDataPoint, tooltipDateLabel } from '../../axisUtils';

interface TooltipProps {
  chartData: ChartDataPoint[] | null,
  comparisonData: ChartDataPoint[] | null,
  alternateData: ChartDataPoint[] | null,
  alternateComparisonData: ChartDataPoint[] | null,
  title: string,
  alternateTitle?: string,
  paddingVertical: number,
  chartHeight: number,
  domainLimit: number,
  // eslint-disable-next-line no-unused-vars
  onClick: (date: Date) => void,
  x?: number,
  y?: number,
  datum?,
  unit?: string,
  alternateColor?: string,
  hideTimeOnTooltip?: boolean,
}

const Tooltip = ({
  chartData, comparisonData, alternateData, alternateComparisonData, title, alternateTitle, datum,
  paddingVertical, chartHeight, domainLimit, onClick, unit, alternateColor, hideTimeOnTooltip, x, y,
}: TooltipProps) => {
  const unitSuffix: string = ` ${unit?.toLowerCase() ?? ''}`;
  const chartDataPoint: ChartDataPoint = chartData?.[datum.index];
  const comparisonDataPoint: ChartDataPoint = comparisonData?.[datum.index];
  const alternateDataPoint: ChartDataPoint = alternateData?.[datum.index];
  const alternateComparisonDataPoint: ChartDataPoint = alternateComparisonData?.[datum.index];
  const chartDataPointDate: string = chartDataPoint?.date;
  const comparisonDataPointDate: string = comparisonDataPoint?.date;
  const alternateDataPointDate: string = alternateDataPoint?.date;
  const alternateComparisonDataPointDate: string = alternateComparisonDataPoint?.date;
  const chartDataPointValue: number = chartDataPoint?.value;
  const comparisonDataPointValue: number = comparisonDataPoint?.value;
  const alternateDataPointValue: number = alternateDataPoint?.value;
  const alternateComparisonDataPointValue: number = alternateComparisonDataPoint?.value;
  const date: Date = (chartDataPointDate || alternateDataPointDate)
    ? new Date(chartDataPointDate || alternateDataPointDate) : null;
  const dateCompared: Date = comparisonDataPointDate ? new Date(comparisonDataPointDate) : null;
  const alternateDateCompared: Date = alternateComparisonDataPointDate
    ? new Date(alternateComparisonDataPointDate) : null;
  const largerDataset = (alternateData?.length ?? 0) > (chartData?.length ?? 0)
    ? alternateData : chartData;
  const dateText: string = date
    ? tooltipDateLabel(largerDataset, datum.index, false, !hideTimeOnTooltip) : '-';
  const dateComparedText: string = dateCompared
    ? `vs. ${tooltipDateLabel(comparisonData, datum.index, false, !hideTimeOnTooltip)}` : null;
  const alternateDateComparedText: string = alternateDateCompared
    ? `vs. ${tooltipDateLabel(alternateComparisonData, datum.index, false, !hideTimeOnTooltip)}`
    : null;
  let valueText: string = `${title}: ${Number.isFinite(chartDataPointValue)
    ? `${(Math.floor((chartDataPointValue ?? 0) * 10) / 10)}` : '-'}`;
  valueText += unitSuffix;
  let alternateValueText: string = `${alternateTitle}: ${Number.isFinite(alternateDataPointValue)
    ? `${(Math.floor((alternateDataPointValue ?? 0) * 10) / 10)}` : '-'}`;
  alternateValueText += unitSuffix;
  let valueComparedText: string = `${title} (compared): ${Number.isFinite(comparisonDataPointValue)
    ? `${(Math.floor((comparisonDataPointValue ?? 0) * 10) / 10)}` : '-'}`;
  valueComparedText += unitSuffix;
  let alternateValueComparedText: string = `${alternateTitle} (compared): ${Number
    .isFinite(alternateComparisonDataPointValue)
    ? `${(Math.floor((alternateComparisonDataPointValue ?? 0) * 10) / 10)}` : '-'}`;
  alternateValueComparedText += unitSuffix;
  let containerHeight: number = comparisonData ? 96 : 56;
  if (alternateData) containerHeight += comparisonData ? 40 : 20;
  const containerWidth: number = (comparisonData ? 100 : 50)
    + Math.max(
      valueText.length,
      dateText.length,
      dateComparedText?.length ?? 0,
      alternateDateComparedText?.length ?? 0,
      valueComparedText?.length ?? 0,
      alternateValueText?.length ?? 0,
      alternateValueComparedText?.length ?? 0,
    ) * 6;
  const containerX: number = x > containerWidth + 20 ? x - containerWidth - 10 : x + 10;
  let containerY: number = Math.max(Math.min(y - containerHeight, 210 - containerHeight), 10);
  if (!Number.isFinite(containerY)) containerY = 10;

  const spot = (cy: number, fill: string) => <circle
    r={6}
    cx={containerX + 15}
    cy={containerY + cy}
    {...{ fill }}
  />;

  const label = (cy: number, text: string) => <text
    x={containerX + 30}
    y={containerY + cy}
    fontFamily={'Roboto_400Regular'}
    fontSize={12}
    height={16}
    strokeWidth={0}
    letterSpacing={0.15}
    fill={Colors.light.hereworksBlack400}>
    {text}
  </text>;

  const dateLabel = (cy: number, text: string) => <text
    x={containerX + 10}
    y={containerY + cy}
    fontFamily={'Poppins_500Medium'}
    fontSize={12}
    height={16}
    strokeWidth={0}
    letterSpacing={0.15}
    fill={Colors.light.hereworksBlack400}>
    {text}
  </text>;

  return <svg>
    <defs>
      <filter id={'f3_desk_bookings'} x={-0.5} y={-1} width='200%' height='250%'>
        <feOffset result='offOut' in='SourceGraphic' dx='0' dy='0' />
        <feColorMatrix result='matrixOut'
          in='offOut'
          type='matrix'
          values='0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.2 0' />
        <feGaussianBlur result='blurOut' in='matrixOut' stdDeviation='4' />
        <feBlend in='SourceGraphic' in2='blurOut' mode='normal' result='colored' />
      </filter>
    </defs>
    <Point
      cx={x}
      value={chartDataPointValue}
      stroke={selectedLineColor}
      limit={domainLimit}
      onClick={onClick ? () => onClick(date) : null}
      {...{ paddingVertical, chartHeight }}
    />
    {alternateData && <Point
      cx={x}
      value={alternateDataPointValue}
      stroke={alternateColor ?? alternateLineColor}
      limit={domainLimit}
      onClick={onClick ? () => onClick(date) : null}
      {...{ paddingVertical, chartHeight }}
    />}
    {comparisonData && <Point
      cx={x}
      value={comparisonDataPointValue}
      stroke={comparisonLineColor}
      limit={domainLimit}
      onClick={onClick ? () => onClick(dateCompared) : null}
      {...{ paddingVertical, chartHeight }}
    />}
    {alternateComparisonData && <Point
      cx={x}
      value={alternateComparisonDataPointValue}
      stroke={alternateComparedLineColor}
      limit={domainLimit}
      onClick={onClick ? () => onClick(alternateDateCompared) : null}
      {...{ paddingVertical, chartHeight }}
    />}
    <rect
      x={containerX}
      y={containerY}
      width={containerWidth}
      height={containerHeight}
      stroke={selectedLineColor}
      rx={5}
      fill='white'
      filter={'url(#f3_desk_bookings)'}
    />
    {datum.date && dateLabel(20, dateText)}
    {comparisonData ? (comparisonDataPointDate && dateLabel(40, dateComparedText))
      : (alternateComparisonData && alternateComparisonDataPointDate
        && dateLabel(40, alternateDateComparedText))}
    <line
      x1={x}
      x2={x}
      y1={paddingVertical}
      y2={chartHeight - paddingVertical + 15}
      stroke={Colors.light.hereworksBlue100}
      strokeDasharray='10,10'
      strokeWidth={1}
    />
    {spot(comparisonData ? 60.5 : 40.5, selectedLineColor)}
    {label(comparisonData ? 65 : 45, valueText)}
    {alternateData && <>
      {spot(comparisonData ? 80.5 : 60.5, alternateColor ?? alternateLineColor)}
      {label(comparisonData ? 85 : 65, alternateValueText)}
    </>}
    {comparisonData && <>
      {spot(alternateData ? 100.5 : 80.5, comparisonLineColor)}
      {label(alternateData ? 105 : 85, valueComparedText)}
    </>}
    {alternateComparisonData && <>
      {spot(120.5, alternateComparedLineColor)}
      {label(125, alternateValueComparedText)}
    </>}
  </svg>;
};

export default Tooltip;
